import React, { useCallback, useEffect, useState } from "react"

import Box from "@mui/material/Box"
import { CircularProgress, IconButton, Skeleton, Slide, Snackbar } from "@mui/material"
import SnoozeIcon from "@mui/icons-material/Snooze"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import { ChevronLeft, ChevronRight } from "@mui/icons-material"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import FormLabel from "@mui/material/FormLabel"
import OutlinedInput from "@mui/material/OutlinedInput"
import Card from "@mui/material/Card"
import Grid from "@mui/material/Grid2"
import Alert from "@mui/material/Alert"
import dayjs from "dayjs"
import "dayjs/locale/fr"

import EmailModal from "./EmailModal"
import { usePdfReceipt } from "../PdfDocument/usePdfReceipt"


const CustomSnackbar = ({ open, setOpen, message, severity }) => (
    <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={Slide}
        open={open}
        onClose={() => setOpen(false)}
        autoHideDuration={4000}
    >
        <Alert onClose={() => setOpen(false)} severity={severity} variant="outlined" sx={{ width: "100%" }}>
            {message}
        </Alert>
    </Snackbar>
)


// ---------------------------------------------------------------------------
// Main component
//
// Props
//   supabase    – initialised Supabase client
//   propertyId  – (optional) UUID of a specific property.
//                 When provided (property page): filters results to this property
//                 and shows an explicit "up to date" state when nothing is pending.
//                 When omitted (dashboard): behaves as before — shows all overdue
//                 payments across the portfolio and returns null when the list is
//                 empty so the dashboard card disappears.
// ---------------------------------------------------------------------------
const PaymentCard = ({ supabase, propertyId = null }) => {

    const [transactions,  setTransactions]  = useState([])
    const [loading,       setLoading]       = useState(true)
    const [fetchError,    setFetchError]    = useState(null)
    const [emailTemplate, setEmailTemplate] = useState("")
    const [receiptEmailTemplate, setReceiptEmailTemplate] = useState("")

    const [navIndex,      setNavIndex]      = useState(0)
    const [modalOpen,     setModalOpen]     = useState(false)
    const [saving,        setSaving]        = useState(false)
    const [snackbar,      setSnackbar]      = useState({ open: false, message: "", severity: "success" })
    const [paymentDate,   setPaymentDate]   = useState(() => new Date().toISOString().split("T")[0])
    const [paymentValues, setPaymentValues] = useState({})

    // Quittance ("rent receipt") email — offered right after a successful save,
    // mirroring the "Relancer" flow but pre-loaded with a PDF attachment.
    const [quittanceModalOpen, setQuittanceModalOpen] = useState(false)
    const [quittanceEmail, setQuittanceEmail] = useState({ to: "", subject: "", message: "", attachment: null })

    const { buildReceiptBlob } = usePdfReceipt(supabase)


    // -----------------------------------------------------------------------
    // Data fetching
    // -----------------------------------------------------------------------
    const fetchTransactions = useCallback(async () => {
        setLoading(true)
        setFetchError(null)

        let query = supabase
            .from("v_overdue_transactions_first")
            .select("lease_id, property_id, property_short_name, period_from, period_to, buckets, tenant_names")

        if (propertyId) query = query.eq("property_id", propertyId)

        const { data, error } = await query

        if (error) {
            setFetchError(`Impossible de charger les loyers dus : ${error.message}`)
        } else {
            setTransactions(data ?? [])
        }
        setLoading(false)
    }, [supabase, propertyId])

    useEffect(() => { fetchTransactions() }, [fetchTransactions])

    useEffect(() => {
        supabase.from("setting").select("value").eq("label", "email_template_rent_reminder").maybeSingle()
            .then(({ data }) => { if (data?.value) setEmailTemplate(data.value) })
    }, [supabase])

    useEffect(() => {
        supabase.from("setting").select("value").eq("label", "email_template_rent_receipt").maybeSingle()
            .then(({ data }) => { if (data?.value) setReceiptEmailTemplate(data.value) })
    }, [supabase])

    // Clamp navIndex if the list shrinks after a save
    useEffect(() => {
        setNavIndex((i) => Math.min(i, Math.max(0, transactions.length - 1)))
    }, [transactions.length])

    // Reset payment values to defaults when navigating
    useEffect(() => {
        if (!transactions[navIndex]) return
        const initial = {}
        transactions[navIndex].buckets.forEach((b) => { initial[b.id] = b.default_value })
        setPaymentValues(initial)
    }, [transactions, navIndex])


    // -----------------------------------------------------------------------
    // Actions
    // -----------------------------------------------------------------------
    const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity })

    const savePayment = async () => {
        const tx = transactions[navIndex]
        if (!tx) return
        setSaving(true)
        const { error } = await supabase.rpc("save_payment", {
            p_period_from:   tx.period_from,
            p_period_to:     tx.period_to,
            p_payment_date:  paymentDate,
            p_bucket_values: tx.buckets.map((b) => ({
                bucket_id: b.id,
                value:     parseFloat(paymentValues[b.id] ?? b.default_value),
            })),
        })

        if (error) {
            setSaving(false)
            const msg =
                error.code === "23503" ? "Enregistrement impossible : un bucket référencé est introuvable." :
                    error.code === "23505" ? "Cette transaction existe déjà pour la période sélectionnée." :
                        `Erreur lors de l'enregistrement : ${error.message}`
            showSnackbar(msg, "error")
            return
        }

        showSnackbar("Paiement enregistré avec succès")
        await fetchTransactions()

        // Offer to send the receipt ("quittance") to the tenant — same idea as
        // "Relancer", except this modal comes pre-loaded with a PDF attachment.
        // `tx` is captured from before the refetch, so it still points at the
        // period that was just paid even though `transactions` has moved on.
        await prepareQuittanceEmail(tx)
        setSaving(false)
    }

    const prepareQuittanceEmail = async (tx) => {
        const result = await buildReceiptBlob(tx.lease_id, tx.period_from)
        if (!result) {
            // The payment itself was saved fine — only the receipt PDF failed
            // to generate, so we just skip the "send it" offer.
            showSnackbar("Paiement enregistré, mais la quittance n'a pas pu être générée.", "warning")
            return
        }
        const { blob, filename, data } = result

        // Recipient addresses are a concern of *this* email flow, not of PDF
        // rendering, so they're fetched here rather than via fetchReceiptData.
        // Only person-tenants have an email in the schema; company tenants are
        // silently skipped.
        const { data: tenantRows } = await supabase
            .from("tenant")
            .select("person:person_id (email)")
            .eq("lease_id", tx.lease_id)
        const tenantEmails = (tenantRows ?? [])
            .map((t) => t.person?.email)
            .filter(Boolean)
            .join(", ")

        const message = receiptEmailTemplate
            .replace("{{ locataires }}", data.tenants ?? "")
            .replace("{{ loyer_du_le }}", dayjs(tx.period_from).locale("fr").format("DD MMMM YYYY"))
            .replace("{{ gestionnaire }}", "Victor Masiak")

        setQuittanceEmail({
            to:      tenantEmails,
            subject: `Quittance de loyer - ${dayjs(tx.period_from).locale("fr").format("MMMM YYYY")}`,
            message,
            attachment: { filename, blob },
        })
        setQuittanceModalOpen(true)
    }


    // -----------------------------------------------------------------------
    // Render: loading
    // -----------------------------------------------------------------------
    if (loading) return (
        <Card variant="outlined">
            <Box sx={{ display: "flex", flexDirection: "row", p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                <Skeleton variant="text" width={120} height={30} sx={{ ml: 1, mr: "auto" }} />
                <Skeleton variant="circular" width={32} height={32} sx={{ mr: 1 }} />
                <Skeleton variant="circular" width={32} height={32} sx={{ mr: 1 }} />
                <Skeleton variant="circular" width={32} height={32} />
            </Box>
            <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={{ xs: 5 }}>
                    <Stack direction="column" spacing={1}>
                        <Skeleton variant="text" width="80%" height={24} />
                        <Skeleton variant="text" width="60%" height={20} />
                        <Skeleton variant="rounded" height={36} />
                        <Skeleton variant="rounded" height={36} />
                    </Stack>
                </Grid>
                <Grid size={{ xs: 7 }}>
                    <Stack spacing={1.5}>
                        {[0, 1, 2].map((i) => (
                            <Box key={i} sx={{ display: "flex", gap: 1 }}>
                                <Skeleton variant="text" width="40%" height={32} />
                                <Skeleton variant="rounded" width="55%" height={32} />
                            </Box>
                        ))}
                    </Stack>
                </Grid>
            </Grid>
        </Card>
    )


    // -----------------------------------------------------------------------
    // Render: fetch error
    // -----------------------------------------------------------------------
    if (fetchError) return <Alert severity="error" variant="outlined">{fetchError}</Alert>


    // -----------------------------------------------------------------------
    // Render: nothing overdue
    //
    // DECISION: behaviour differs by context.
    //   Dashboard (no propertyId): return null so the card disappears.
    //   Property page (propertyId set): show an explicit "up to date" indicator
    //     so the actions section always gives clear feedback.
    // -----------------------------------------------------------------------
    if (transactions.length === 0) {
        return (
            <Card variant="outlined" sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <CheckCircleOutlineIcon color="success" sx={{ flexShrink: 0 }} />
                <Box>
                    <Typography variant="body2" fontWeight={600}>Paiements à jour</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {propertyId
                            ? "Aucun paiement en attente pour ce bien."
                            : "Aucun paiement en attente."
                        }
                    </Typography>
                </Box>
            </Card>
        )
    }

    const tx = transactions[navIndex]

    const emailMessage = emailTemplate
        .replace("{{ locataires }}", tx?.tenant_names ?? "")
        .replace("{{ loyer_du_le }}", tx ? dayjs(tx.period_from).locale("fr").format("DD MMMM YYYY") : "")
        .replace("{{ gestionnaire }}", "Victor Masiak")

    // Navigation is only meaningful when there are multiple items to browse.
    const canNavigate = transactions.length > 1

    return (
        <>
            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
            <EmailModal
                supabase={supabase}
                open={modalOpen}
                setOpen={setModalOpen}
                title="Email de relance"
                emailTo={"victor.masiak@gmail.com"}
                emailSubject="Relance loyer"
                emailMessage={emailMessage}
                successMessage="L'email de relance a bien été envoyé"
                onSuccess={(msg) => showSnackbar(msg)}
                onError={(msg) => showSnackbar(msg, "error")}
            />
            <EmailModal
                supabase={supabase}
                open={quittanceModalOpen}
                setOpen={setQuittanceModalOpen}
                title="Envoi de la quittance"
                emailTo={quittanceEmail.to}
                emailSubject={quittanceEmail.subject}
                emailMessage={quittanceEmail.message}
                attachment={quittanceEmail.attachment}
                successMessage="La quittance a bien été envoyée"
                onSuccess={(msg) => showSnackbar(msg)}
                onError={(msg) => showSnackbar(msg, "error")}
            />
            <Card sx={{ padding: 0 }} variant="outlined">
                <Box sx={{ display: "flex", flexDirection: "row", backgroundColor: "background.paper", p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                    <Typography component="p" sx={{ fontSize: 16, fontWeight: 600, marginRight: "auto", marginY: "auto", paddingLeft: 1 }}>
                        Loyer dû
                        {canNavigate && (
                            <Typography
                                component="span"
                                sx={{ ml: 1, fontSize: 13, fontWeight: 400, color: "text.secondary" }}
                            >
                                ({navIndex + 1} / {transactions.length})
                            </Typography>
                        )}
                    </Typography>
                    {/* Snooze + navigation — hidden when scoped to a single property. */}
                    {!propertyId && (
                        <>
                            <IconButton sx={{ marginRight: 1 }} size="small"><SnoozeIcon /></IconButton>
                            <IconButton
                                sx={{ marginRight: 1 }}
                                size="small"
                                onClick={() => setNavIndex((i) => (i - 1 + transactions.length) % transactions.length)}
                                disabled={!canNavigate || navIndex === 0}
                            >
                                <ChevronLeft />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => setNavIndex((i) => (i + 1) % transactions.length)}
                                disabled={!canNavigate || navIndex === transactions.length - 1}
                            >
                                <ChevronRight />
                            </IconButton>
                        </>
                    )}
                </Box>
                <Grid container spacing={2} sx={{ p: 2 }}>
                    <Grid size={{ xs: 5 }}>
                        <Stack direction="column" spacing={1}>
                            <Box>
                                {/* DECISION: hide property name when scoped to a single property —
                                    the user already knows which property they're on. */}
                                {!propertyId && (
                                    <Typography component="p" sx={{ fontSize: 16, fontWeight: 600 }}>
                                        {tx.property_short_name}
                                    </Typography>
                                )}
                                <Typography component="p">
                                    Dû le {dayjs(tx.period_from).locale("fr").format("DD MMM")}
                                </Typography>
                            </Box>
                            <Button variant="contained" sx={{ width: "100%" }} onClick={savePayment} disabled={saving}>
                                {saving ? <CircularProgress size={20} color="inherit" /> : "Enregistrer"}
                            </Button>
                            <Button variant="outlined" sx={{ width: "100%" }} onClick={() => setModalOpen(true)} disabled={saving}>
                                Relancer
                            </Button>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 7 }}>
                        <table style={{ width: "100%" }}>
                            <tbody>
                            <tr>
                                <td><FormLabel htmlFor="paymentDate" sx={{ mr: 1 }}>Reçu le</FormLabel></td>
                                <td>
                                    <OutlinedInput sx={{ width: "100%" }} id="paymentDate" name="paymentDate" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} size="small" />
                                </td>
                            </tr>
                            {tx.buckets.slice().sort((a, b) => a.category.localeCompare(b.category)).map((bucket) => (
                                <tr key={bucket.id}>
                                    <td><FormLabel htmlFor={`payment-bucket-${bucket.id}`} sx={{ mr: 1 }}>{bucket.category}</FormLabel></td>
                                    <td>
                                        <OutlinedInput
                                            id={`payment-bucket-${bucket.id}`}
                                            name={`payment-bucket-${bucket.id}`}
                                            placeholder="123"
                                            endAdornment="€"
                                            value={paymentValues[bucket.id] ?? bucket.default_value}
                                            onChange={(e) => setPaymentValues((v) => ({ ...v, [bucket.id]: e.target.value }))}
                                            size="small"
                                            type="number"
                                        />
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </Grid>
                </Grid>
            </Card>
        </>
    )
}

export default PaymentCard